import { useState } from 'react';
import { useLocation } from 'react-router-dom';

const appName = process.env.REACT_APP_NAME;
let brandLogo = null;
try {
  brandLogo = require(`@/assets/images/${appName}/horizontal-logo.svg`);
} catch (e) {
  brandLogo = null;
}

function CheckCircleIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-16 w-16 text-green-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <rect
        x="9"
        y="9"
        width="13"
        height="13"
        rx="2"
        ry="2"
      />
      <path
        strokeLinecap="round"
        d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
      />
    </svg>
  );
}

function SmallCheckIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

export default function PublicRegistrationSuccess() {
  const { state } = useLocation();
  const protocol = state?.protocol;
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (!protocol) return;
    navigator.clipboard.writeText(protocol).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Hero (green tint) ── */}
      <header className="bg-gradient-to-br from-slate-900 via-green-900 to-green-800">
        <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          {brandLogo && (
            <img
              src={brandLogo}
              alt={appName}
              className="mb-10 h-14 w-auto rounded-md bg-white/95 px-4 py-2.5 shadow-sm"
            />
          )}
          <div className="inline-flex items-center gap-1.5 rounded-full bg-green-500/20 px-3 py-1 text-xs font-semibold text-green-300 ring-1 ring-green-400/30">
            <span
              className="h-1.5 w-1.5 rounded-full bg-green-400"
              aria-hidden="true"
            />
            Inscrição enviada
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">Inscrição confirmada!</h1>
          <p className="mt-3 text-lg text-green-100">Seu cadastro foi recebido com sucesso. Fique atento ao email de confirmação.</p>
        </div>
      </header>

      {/* ── Content card ── */}
      <div className="relative mx-auto -mt-8 max-w-3xl px-4 pb-6 sm:px-6">
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200/50">
          {/* icon + heading */}
          <div className="flex flex-col items-center px-6 pb-8 pt-10 text-center sm:px-8">
            <CheckCircleIcon />
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Tudo certo!</h2>
            <p className="mt-2 max-w-md text-slate-500">Sua inscrição foi registrada. Guarde o número de protocolo abaixo — você precisará dele para acompanhar o processo seletivo.</p>
          </div>

          {/* protocol box */}
          {protocol && (
            <div className="border-t border-slate-200 px-6 py-7 sm:px-8">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Número de protocolo</p>
              <div className="flex items-center gap-3">
                <code className="flex-1 select-all rounded-lg bg-slate-100 px-4 py-3 font-mono text-lg font-semibold tracking-wider text-slate-900">{protocol}</code>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {copied ? <SmallCheckIcon /> : <CopyIcon />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <p className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2.5 text-sm text-amber-700">Anote este protocolo. Você precisará dele para acompanhar sua inscrição.</p>
            </div>
          )}

          {/* next steps */}
          <div className="border-t border-slate-200 bg-slate-50 px-6 py-7 sm:px-8">
            <p className="mb-3 text-sm font-semibold text-slate-700">Próximos passos</p>
            <ul className="space-y-2.5">
              {[
                'Verifique sua caixa de entrada — você receberá um email de confirmação.',
                'Aguarde o contato da unidade de ensino com as informações do processo seletivo.',
                'Em caso de dúvidas, entre em contato com sua regional informando o número de protocolo.'
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-slate-600"
                >
                  <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <footer className="mx-auto max-w-3xl px-4 pb-12 pt-4 text-center sm:px-6">
        <p className="text-sm text-slate-500">Dúvidas? Entre em contato com sua regional.</p>
      </footer>
    </div>
  );
}
